/**
 * Hook for connecting to the Kafka event stream via SSE.
 * Only connects in 'live' mode. Auto-disconnects on unmount.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useDataMode } from '../providers/DataModeProvider';
import type { EventLogEntry } from '../types/metrics';
import {
  type ConnectionStatus,
  connectEventStream,
} from '../services/eventStream';

interface UseEventStreamOptions {
  /** Maximum events to keep in state */
  maxEvents?: number;
  /** Only connect when this is true (defaults to live mode check) */
  enabled?: boolean;
}

interface UseEventStreamReturn {
  /** Live events from Kafka */
  events: EventLogEntry[];
  /** SSE connection status */
  status: ConnectionStatus;
  /** Number of events received since last clear */
  eventCount: number;
  /** Clear all events */
  clearEvents: () => void;
}

export function useEventStream(
  options: UseEventStreamOptions = {}
): UseEventStreamReturn {
  const { maxEvents = 50 } = options;
  const { mode } = useDataMode();
  const enabled = options.enabled ?? mode === 'live';

  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const eventCountRef = useRef(0);
  const [eventCount, setEventCount] = useState(0);

  const handleEvent = useCallback(
    (entry: EventLogEntry) => {
      setEvents((prev) => [entry, ...prev].slice(0, maxEvents));
      eventCountRef.current++;
      setEventCount(eventCountRef.current);
    },
    [maxEvents]
  );

  const clearEvents = useCallback(() => {
    setEvents([]);
    eventCountRef.current = 0;
    setEventCount(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return;
    }

    const disconnect = connectEventStream(handleEvent, setStatus);

    return () => {
      disconnect();
    };
  }, [enabled, handleEvent]);

  return { events, status, eventCount, clearEvents };
}
